import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import {
  CreateDemandForecastRequest,
  UpdateDemandForecastRequest,
  DemandForecastQueryParams,
  CalculateForecastRequest,
  GenerateForecastsRequest,
} from '../types/demand-forecasting.types';
import { demandForecastingService } from '../services/demand-forecasting.service';

export const useDemandForecastsPage = (params: DemandForecastQueryParams) => {
  return useQuery({
    queryKey: ['demandForecasts', 'page', params],
    queryFn: () => demandForecastingService.getPage(params),
    placeholderData: keepPreviousData,
  });
};

export const useDemandForecastSummary = () => {
  return useQuery({
    queryKey: ['demandForecasts', 'summary'],
    queryFn: () => demandForecastingService.getSummary(),
  });
};

// The forecast the server would calculate for a form that has not been saved yet
export const useForecastCalculation = (request: CalculateForecastRequest | null) => {
  return useQuery({
    queryKey: ['demandForecastCalculation', request],
    queryFn: () => demandForecastingService.calculateForecasts(request as CalculateForecastRequest),
    enabled: !!request,
    placeholderData: keepPreviousData,
    retry: false,
  });
};

const useInvalidateDemandForecasts = () => {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['demandForecasts'] });
};

export const useCreateDemandForecast = () => {
  const invalidate = useInvalidateDemandForecasts();
  return useMutation({
    mutationFn: (data: CreateDemandForecastRequest) => demandForecastingService.createForecast(data),
    onSuccess: invalidate,
  });
};

export const useGenerateDemandForecasts = () => {
  const invalidate = useInvalidateDemandForecasts();
  return useMutation({
    mutationFn: (data: GenerateForecastsRequest) => demandForecastingService.generateForecasts(data),
    onSuccess: invalidate,
  });
};

export const useUpdateDemandForecast = () => {
  const invalidate = useInvalidateDemandForecasts();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDemandForecastRequest }) =>
      demandForecastingService.updateForecast(id, data),
    onSuccess: invalidate,
  });
};

export const useDeleteDemandForecast = () => {
  const invalidate = useInvalidateDemandForecasts();
  return useMutation({
    mutationFn: (id: string) => demandForecastingService.deleteForecast(id),
    onSuccess: invalidate,
  });
};

export const useRecordActualDemand = () => {
  const invalidate = useInvalidateDemandForecasts();
  return useMutation({
    mutationFn: ({ id, actualDemand }: { id: string; actualDemand: number }) =>
      demandForecastingService.updateActualDemand(id, actualDemand),
    onSuccess: invalidate,
  });
};
